// The shell's navigation: a menu, not a row of links.
//
// Wide, it is the entries side by side with the current one underlined; NavLink sets
// aria-current on that one, and the stylesheet reads it, so "which is active" is the router's
// answer and not a second copy kept here. Narrow, the same entries sit behind a button named
// Menu: open on click (which is also Enter and Space on a button), closed on Escape and on
// choosing an entry - choosing navigates, and a menu still open over the new screen is in the way.

import { useEffect, useState } from 'react';
import { NavLink } from 'react-router';

import Icon from './Icon.jsx';
import useMediaQuery from './useMediaQuery.js';

// The width below which the entries no longer fit beside the mark and the user block. The same
// breakpoint the shell uses to drop its side margins.
const COLLAPSE_BELOW = '(max-width: 48rem)';

export default function Navigation({ entries }) {
  const collapsed = useMediaQuery(COLLAPSE_BELOW);
  const [open, setOpen] = useState(false);

  // Widening past the breakpoint with the menu open would leave it "open" invisibly, and the next
  // narrowing would show it without anyone having asked.
  useEffect(() => {
    if (!collapsed) setOpen(false);
  }, [collapsed]);

  const onKeyDown = (event) => {
    if (event.key === 'Escape' && open) {
      setOpen(false);
      event.currentTarget.querySelector('button')?.focus();
    }
  };

  return (
    <nav className={`menu${collapsed ? ' collapsed' : ''}`} aria-label="Main" onKeyDown={onKeyDown}>
      {collapsed && (
        <button type="button" className="icon-button quiet" aria-label="Menu"
                aria-expanded={open} aria-controls="main-menu" onClick={() => setOpen((v) => !v)}>
          <Icon name={open ? 'close' : 'menu'} />
        </button>
      )}
      {(!collapsed || open) && (
        <ul id="main-menu">
          {entries.map(({ to, label }) => (
            <li key={to}>
              <NavLink to={to} onClick={() => setOpen(false)}>{label}</NavLink>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}
